import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from '../../common/withRouter';
import { Loader } from '../../web.common';
import { LayoutContext } from '../layout/siteLayout';
import PageTop from '../layout/PageTop';
import withApi from '../higherOrderComponents/withApi';
import { formatCourtTime } from '../../common/copenhagenTime';

// Colour mapping ported from the standalone v0/Vercel overview so the semantics
// stay identical: the cell colour tells WHY a slot is (not) sellable.
function cellStyle(slot) {
  if (!slot) {
    return { background: '#f3f4f6', color: '#9ca3af' };
  }

  const status = (slot.status || '').toLowerCase();

  // Few slots left (Not Available / Not On Sale [x/y], y <= 2) -> amber
  if (status.includes('not available') && status.includes('not on sale')) {
    const m = status.match(/\[(\d+)\/(\d+)\]/);
    if (m) {
      const available = parseInt(m[2], 10);
      if (available > 0 && available <= 2) {
        return { background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' };
      }
    }
  }

  // Available but not on sale -> blue
  if (status.includes('available') && status.includes('not on sale') && !status.includes('not available')) {
    return { background: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe' };
  }

  // Not available but ON sale -> purple (anomaly: on sale with nothing to sell)
  if (status.includes('not available') && status.includes('on sale') && !status.includes('not on sale')) {
    return { background: '#ede9fe', color: '#5b21b6', border: '1px solid #ddd6fe' };
  }

  switch ((slot.color || '').toLowerCase()) {
    case 'green': return { background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' };
    case 'red': return { background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' };
    case 'yellow': return { background: '#fef9c3', color: '#854d0e', border: '1px solid #fef08a' };
    default: return { background: '#f3f4f6', color: '#4b5563', border: '1px solid #e5e7eb' };
  }
}

const LEGEND = [
  { label: 'Available & on sale', style: cellStyle({ status: '', color: 'green' }) },
  { label: 'Available, not on sale', style: cellStyle({ status: 'available not on sale' }) },
  { label: 'Few slots left', style: cellStyle({ status: 'not available not on sale [1/1]' }) },
  { label: 'Occupied / full', style: cellStyle({ status: '', color: 'red' }) },
  { label: 'Not available, ON SALE (anomaly)', style: cellStyle({ status: 'not available on sale' }) },
  { label: 'No slot', style: cellStyle(null) }
];

class SeasonalCourtsOverviewInternal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: [],
      loading: true,
      error: null,
      selectedFacility: '',
      selectedDay: ''
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  fetchData = async () => {
    this.setState({ loading: true, error: null });
    try {
      const res = await this.props.api.seasonalcourts.getCourtsStatus();
      const data = (res && res.data) ? res.data : (res || []);

      const next = { data, loading: false };
      if (Array.isArray(data) && data.length > 0) {
        next.selectedFacility = data[0].facilityName;
        if (data[0].weekdays && data[0].weekdays.length > 0) {
          next.selectedDay = data[0].weekdays[0].weekDay;
        }
      }
      this.setState(next);
    } catch (e) {
      this.setState({ loading: false, error: (e && e.message) || 'Error loading data' });
    }
  };

  render() {
    const { data, loading, error, selectedFacility, selectedDay } = this.state;

    const facilityData = data.find(f => f.facilityName === selectedFacility);
    const weekdays = (facilityData && facilityData.weekdays) || [];
    const dayData = weekdays.find(w => w.weekDay === selectedDay);
    const courts = (dayData && dayData.courts) || [];
    const times = (dayData && dayData.times) || [];

    return (
      <LayoutContext.Consumer>
        {() => (
          <React.Fragment>
            <PageTop
              documentTitle='Seasonal Courts Overview'
              documentDescription='Seasonal courts availability overview'
            />

            <h2 className='main-page-header'>Seasonal Courts Overview</h2>
            <br />

            {error && <div className='alert alert-danger'>{error}</div>}

            {loading ? (
              <Loader hasLoaded={false}>Loading...</Loader>
            ) : (
              <React.Fragment>
                <div className='form-inline' style={{ marginBottom: '1rem', gap: '1rem' }}>
                  <label style={{ marginRight: '.5rem' }}>Facility</label>
                  <select
                    className='form-control'
                    style={{ marginRight: '1.5rem' }}
                    value={selectedFacility}
                    onChange={e => {
                      const f = data.find(x => x.facilityName === e.target.value);
                      const firstDay = f && f.weekdays && f.weekdays[0] ? f.weekdays[0].weekDay : '';
                      this.setState({ selectedFacility: e.target.value, selectedDay: firstDay });
                    }}
                  >
                    {data.map(f => <option key={f.facilityName} value={f.facilityName}>{f.facilityName}</option>)}
                  </select>

                  <label style={{ marginRight: '.5rem' }}>Day</label>
                  <select
                    className='form-control'
                    style={{ marginRight: '1.5rem' }}
                    value={selectedDay}
                    onChange={e => this.setState({ selectedDay: e.target.value })}
                  >
                    {weekdays.map(w => <option key={w.weekDay} value={w.weekDay}>{w.weekDay}</option>)}
                  </select>

                  <button className='btn btn-secondary' onClick={this.fetchData}>Refresh</button>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.75rem', marginBottom: '1rem' }}>
                  {LEGEND.map(l => (
                    <span key={l.label} style={{ display: 'inline-flex', alignItems: 'center', marginRight: '1rem', fontSize: '.85rem' }}>
                      <span style={{ ...l.style, display: 'inline-block', width: 16, height: 16, borderRadius: 3, marginRight: 6 }} />
                      {l.label}
                    </span>
                  ))}
                </div>

                <article className='data-table-wrap' style={{ overflowX: 'auto' }}>
                  <table className='data-lists component-data-table' style={{ borderCollapse: 'separate', borderSpacing: 2 }}>
                    <thead>
                      <tr>
                        <td style={{ fontWeight: 'bold' }}>Court \ time (Copenhagen)</td>
                        {times.map(t => <td key={t} style={{ fontWeight: 'bold', textAlign: 'center' }}>{formatCourtTime(t)}</td>)}
                      </tr>
                    </thead>
                    <tbody>
                      {courts.map(court => {
                        const byTime = {};
                        (court.slots || []).forEach(s => { byTime[s.time] = s; });
                        return (
                          <tr key={court.courtName}>
                            <td style={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{court.courtName}</td>
                            {times.map(t => {
                              const slot = byTime[t] || null;
                              const style = cellStyle(slot);
                              const cell = (
                                <div
                                  title={slot ? (slot.note || slot.status || '') : ''}
                                  style={{ ...style, padding: '4px 6px', borderRadius: 4, textAlign: 'center', minWidth: 70, fontSize: '.75rem' }}
                                >
                                  {slot ? (slot.status || '') : ''}
                                </div>
                              );
                              return (
                                <td key={t} style={{ padding: 1 }}>
                                  {slot && slot.link
                                    ? <a href={slot.link} style={{ textDecoration: 'none' }}>{cell}</a>
                                    : cell}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </article>
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </LayoutContext.Consumer>
    );
  }
}

const SeasonalCourtsOverview = withRouter(withApi(connect(({ user }) =>
  ({ isLoggedIn: !!user }), {})(SeasonalCourtsOverviewInternal)));

SeasonalCourtsOverview.requiresLogin = true;
SeasonalCourtsOverview.propTypes = { isLoggedIn: PropTypes.bool };

export default SeasonalCourtsOverview;
